import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId, scrollToIdWhenReady } from "./scroll";
import { trackEvent } from "./track";
import { PORTAL_URL } from "./applyUrl";
import { useI18n } from "../../i18n";
import LanguageSwitcher from "./LanguageSwitcher";

const linkIds = ["services", "solutions", "approche", "consultant", "faq", "contact"];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToIdWhenReady(id), 250);
    } else {
      setTimeout(() => scrollToId(id), open ? 300 : 0);
    }
  };

  return (
    <>
      <header className="glass-nav fixed top-0 left-0 right-0 z-50" data-testid="main-nav">
        <div className="max-w-[1536px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <button
            onClick={() => go("hero")}
            className="flex flex-shrink-0 items-center gap-3 font-heading font-bold text-lg tracking-tight text-white whitespace-nowrap"
            data-testid="nav-logo"
          >
            <img src="/logo-cs.png" alt="Logo Consultants Succès" className="h-9 w-auto" />
            CONSULTANTS <span className="text-gold">SUCCÈS</span>
          </button>

          <nav className="hidden xl:flex items-center gap-5">
            {linkIds.map((id) => (
              <button key={id} onClick={() => go(id)} className="nav-link" data-testid={`nav-link-${id}`}>
                {t(`nav.${id}`)}
              </button>
            ))}
            <Link to="/impact-social" className="nav-link" data-testid="nav-link-impact">
              {t("nav.impact")}
            </Link>
          </nav>

          <div className="hidden md:flex flex-shrink-0 items-center gap-3">
            <LanguageSwitcher />
            {PORTAL_URL && (
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost hidden min-[1600px]:inline-flex items-center gap-2 px-4 py-2.5 text-sm whitespace-nowrap"
                onClick={() => trackEvent("merchant_portal_click", { placement: "nav" })}
                data-testid="nav-portal-cta"
              >
                {t("nav.portal")}
              </a>
            )}
            <a
              href="https://vytalimarketing.com"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold inline-flex items-center gap-2 px-4 py-2.5 text-sm whitespace-nowrap"
              onClick={() => trackEvent("click_decouvrir_vytali", { location: "nav" })}
              data-testid="nav-vytali-cta"
            >
              {t("nav.vytali")} <ArrowUpRight size={16} />
            </a>
          </div>

          <button
            className="xl:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label={t("nav.ariaMenu")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 xl:hidden flex flex-col justify-center px-8"
            style={{ background: "#0F2027", paddingTop: "72px" }}
            data-testid="mobile-menu-overlay"
          >
            <div className="flex flex-col gap-6">
              {linkIds.map((id, i) => (
                <motion.button
                  key={id}
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                  onClick={() => go(id)}
                  className="font-heading text-3xl font-bold text-white text-left"
                  data-testid={`mobile-nav-link-${id}`}
                >
                  {t(`nav.${id}`)}
                </motion.button>
              ))}
              <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.36, duration: 0.4 }}
              >
                <Link
                  to="/impact-social"
                  onClick={() => setOpen(false)}
                  className="font-heading text-3xl font-bold text-white block text-left"
                  data-testid="mobile-nav-link-impact"
                >
                  {t("nav.impact")}
                </Link>
              </motion.div>
              <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                className="mt-2"
                data-testid="mobile-language-switcher"
              >
                <LanguageSwitcher size="lg" />
              </motion.div>
              {PORTAL_URL && (
                <motion.a
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.44, duration: 0.4 }}
                  href={PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost inline-flex items-center justify-center gap-2 px-6 py-4 text-base mt-2"
                  onClick={() => trackEvent("merchant_portal_click", { placement: "mobile_menu" })}
                  data-testid="mobile-portal-cta"
                >
                  {t("nav.portal")}
                </motion.a>
              )}
              <motion.a
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.48, duration: 0.4 }}
                href="https://vytalimarketing.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-4 text-base mt-2"
                onClick={() => trackEvent("click_decouvrir_vytali", { location: "mobile_menu" })}
                data-testid="mobile-vytali-cta"
              >
                {t("nav.vytali")} <ArrowUpRight size={18} />
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
